'use client';

import { motion, Variants } from 'framer-motion';
import Image from 'next/image';
import React from 'react';
import { Section } from '~/data/modules/types';
import Highlighter from '../../AnimatedComponents/Highlighter';

interface ModuleSectionProps {
    className?: string;
    sections?: Section[];
}

const cardVariants: Variants = {
    hidden: { opacity: 0, y: 25 },
    visible: (index: number) => ({
        opacity: 1,
        y: 0,
        transition: {
            duration: 0.2,
            ease: 'easeOut',
            delay: index * 0.05
        }
    })
};

const headerVariants: Variants = {
    hidden: { opacity: 0, y: 15 },
    visible: {
        opacity: 1,
        y: 0,
        transition: { duration: 0.3, ease: 'easeOut' }
    }
};

const ImageCard = React.memo(({ element, index }: { element: any; index: number }) => (
    <motion.div
        variants={cardVariants}
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, amount: 0.2 }}
        custom={index}
        whileHover="hover"
        className="tw-group/card tw-relative tw-flex tw-h-48 tw-flex-col tw-gap-3 tw-overflow-hidden tw-rounded-xl tw-shadow-m tw-transition-all tw-duration-300 md:tw-col-span-2 md:tw-h-72"
    >
        <motion.div
            transition={{ duration: 0.4, ease: 'easeOut' }}
            className="tw-relative tw-h-full tw-w-full tw-transition-all tw-duration-200 group-hover/card:tw-scale-105"
        >
            <Image
                src={element.image}
                alt={element.title ?? 'Module Image'}
                fill
                className="tw-object-cover tw-object-left-top"
            />
        </motion.div>
    </motion.div>
));

ImageCard.displayName = 'ImageCard';

const InfoCard = React.memo(({ element, index }: { element: any; index: number }) => (
    <motion.div
        variants={cardVariants}
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, amount: 0.2 }}
        custom={index}
        className="tw-group/card"
    >
        <div className="tw-flex tw-h-full tw-flex-col tw-gap-3 tw-overflow-visible tw-rounded-xl tw-p-4 tw-shadow-m tw-transition-all tw-duration-300">
            <div className="tw-flex tw-flex-1 tw-flex-col tw-gap-4">
                {element.title && (
                    <div className="tw-flex tw-items-center tw-gap-1">
                        <h3 className="tw-m-0 tw-text-lg tw-font-semibold tw-text-accents-9">{element.title}</h3>
                    </div>
                )}

                {element.description && (
                    <p className="tw-m-0 tw-text-sm tw-leading-relaxed tw-text-accents-7">{element.description}</p>
                )}
            </div>
        </div>
    </motion.div>
));

InfoCard.displayName = 'InfoCard';

export const ModuleSection: React.FC<React.PropsWithChildren<ModuleSectionProps>> = ({
    sections = [],
    className,
    children
}) => {
    if (!sections || sections.length === 0) return null;

    return (
        <div className={className}>
            {children && (
                <motion.div
                    initial="hidden"
                    whileInView="visible"
                    viewport={{ amount: 0.1, once: true }}
                    variants={headerVariants}
                    className="tw-mb-4"
                >
                    {children}
                </motion.div>
            )}

            {sections.map((section, sectionIndex) => (
                <section key={sectionIndex} className="tw-group tw-relative last:tw-mb-0">
                    <div className="tw-relative tw-transition-all">
                        {section.title && (
                            <motion.div
                                className="tw-mb-6"
                                variants={headerVariants}
                                initial="hidden"
                                whileInView="visible"
                                viewport={{ amount: 0.2, once: true }}
                            >
                                <h2 className="tw-mb-4 tw-ml-2 tw-text-xl tw-font-bold tw-text-accents-9 md:tw-text-2xl">
                                    <Highlighter action={section.highlight} iterations={2}>
                                        {section.title}
                                    </Highlighter>
                                </h2>

                                {section.description && typeof section.description === 'string' && (
                                    <p className="tw-m-0 tw-pl-3 tw-text-base tw-leading-relaxed tw-text-accents-7">
                                        {section.description}
                                    </p>
                                )}

                                {section.description &&
                                    Array.isArray(section.description) &&
                                    section.description.map((desc, descIndex) => (
                                        <p
                                            key={descIndex}
                                            className="tw-mb-4 tw-pl-3 tw-text-base tw-leading-relaxed tw-text-accents-7 md:tw-mb-8"
                                        >
                                            {desc}
                                        </p>
                                    ))}
                            </motion.div>
                        )}

                        {section.elements?.length > 0 && (
                            <div className="tw-grid tw-grid-cols-1 tw-gap-4 md:tw-grid-cols-2 lg:tw-gap-8">
                                {section.elements
                                    ?.sort((a) => (a.type === 'imageCard' ? -1 : 1)) // imageCards first
                                    .map((element, elementIndex) => {
                                        if (element.type === 'imageCard') {
                                            return (
                                                <ImageCard
                                                    key={`${sectionIndex}-${elementIndex}`}
                                                    element={element}
                                                    index={elementIndex}
                                                />
                                            );
                                        }
                                        if (element.type === 'sectionCard') {
                                            return (
                                                <InfoCard
                                                    key={`${sectionIndex}-${elementIndex}`}
                                                    element={element}
                                                    index={elementIndex}
                                                />
                                            );
                                        }
                                        return null;
                                    })}
                            </div>
                        )}
                    </div>
                </section>
            ))}
        </div>
    );
};
